import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import Modal from "react-responsive-modal";
import { Alert, Button, Checkbox, ControlLabel, FormControl, FormGroup, ProgressBar } from "react-bootstrap";

import {
	getDriveObjectsAsMap,
	getFullObjectPath
} from "../../actions/drive";
import {
	attachDriveResourcesToDraft,
	prepareLegacyFloorplanProject,
	validateFloorplanDraft
} from "../../utils/floorplanImportUtils";
import {
	getFloorplanAssetFileName,
	importPreparedFloorplanProject,
	joinFloorplanDrivePath
} from "../../utils/floorplanDriveImportUtils";
import { prepareFloorplanPreviewRenditions } from "../../utils/floorplanPreviewRenditionUtils";

function initialState() {
	return {
		selectedFile: null,
		fileInputKey: 0,
		projectFolderName: "",
		keepLegacySource: false,
		prepared: null,
		preflightValidation: null,
		phase: "idle",
		progress: {
			completed: 0,
			total: 0,
			loaded: 0,
			bytesTotal: 0,
			label: ""
		},
		error: "",
		result: null
	};
}

export function sanitizeProjectFolderName(value = "") {
	return `${value || ""}`
		.replace(/[\\/]/g, "-")
		.split("")
		.filter(character => character.charCodeAt(0) > 31 && character.charCodeAt(0) !== 127)
		.join("")
		.replace(/\s+/g, " ")
		.trim()
		.replace(/^\.+|\.+$/g, "")
		.slice(0, 120);
}

function deriveProjectFolderName(fileName = "") {
	const withoutKnownExtension = `${fileName || ""}`
		.replace(/\.floorplanproject\.json$/i, "")
		.replace(/\.json$/i, "");
	return sanitizeProjectFolderName(withoutKnownExtension) || "Imported Floorplan";
}

function isSupportedFile(file) {
	return !!(file && /(?:\.floorplanproject\.json|\.json)$/i.test(file.name || ""));
}

function getErrorMessage(error, fallback = "The floorplan project could not be imported.") {
	if (!error) {
		return fallback;
	}
	if (error.response && error.response.body && error.response.body.message) {
		return error.response.body.message;
	}
	return error.message || `${error}` || fallback;
}

function asArray(value) {
	return Array.isArray(value) ? value : [];
}

function issueMessage(issue) {
	if (typeof issue === "string") {
		return issue;
	}
	if (!issue || typeof issue !== "object") {
		return `${issue || "Unknown issue"}`;
	}
	return `${issue.path ? `${issue.path}: ` : ""}${issue.message || "Unknown issue"}`;
}

function getResourcePath(resource) {
	if (!resource) {
		return "";
	}
	return Array.isArray(resource.path) ? resource.path.join("/") : `${resource.path || ""}`;
}

function readFileAsText(file, onProgress, registerReader) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		registerReader(reader);
		reader.onerror = () => reject(reader.error || new Error("Unable to read the selected file."));
		reader.onabort = () => reject(new Error("Reading the selected file was cancelled."));
		reader.onprogress = event => {
			if (onProgress) {
				onProgress(event.loaded, event.lengthComputable ? event.total : file.size);
			}
		};
		reader.onload = () => resolve(`${reader.result || ""}`);
		reader.readAsText(file);
	});
}

function countCollection(value) {
	if (Array.isArray(value)) {
		return value.length;
	}
	return value && typeof value === "object" ? Object.keys(value).length : 0;
}

function firstCount(source, keys, fallback = 0) {
	for (let index = 0; index < keys.length; index++) {
		const value = source && source[keys[index]];
		if (Number.isFinite(value)) {
			return value;
		}
		if (value && typeof value === "object") {
			return countCollection(value);
		}
	}
	return fallback;
}

function formatBytes(value) {
	const bytes = Number(value) || 0;
	if (bytes === 0) {
		return "0 B";
	}
	const units = ["B", "KB", "MB", "GB"];
	const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
	return `${(bytes / Math.pow(1024, unitIndex)).toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function normalizeInventory(prepared) {
	const inventory = prepared && prepared.inventory ? prepared.inventory : {};
	const draft = prepared && prepared.draft ? prepared.draft : {};
	const assets = asArray(prepared && prepared.assets);
	const previewInventory = prepared && prepared.renditions && prepared.renditions.inventory
		? prepared.renditions.inventory
		: {};
	return {
		floors: firstCount(inventory, ["floorCount"], countCollection(draft.floors)),
		basePlans: firstCount(inventory, ["basePlanCount"]),
		layers: firstCount(inventory, ["layerCount"]),
		assets: firstCount(inventory, ["uniqueAssetCount"], assets.length),
		bytes: firstCount(inventory, ["uniqueAssetBytes"], assets.reduce((sum, asset) => sum + (Number(asset.byteLength) || 0), 0)),
		previews: firstCount(previewInventory, ["uniquePreviewAssetCount"]),
		previewBytes: firstCount(previewInventory, ["previewBytes"]),
		warnings: asArray(inventory.warnings).concat(asArray(prepared && prepared.warnings))
	};
}

function validatePreparedDraft(prepared, projectPath) {
	const resourcesByAssetId = {};
	asArray(prepared.assets).forEach(asset => {
		resourcesByAssetId[asset.id] = {
			id: `pending-${asset.id}`,
			type: "resource/external/drive",
			guid: `pending-${asset.id}`,
			revision: 0,
			scope: "pending-import",
			metaData: {
				pathHint: joinFloorplanDrivePath(projectPath, "Authoring", "assets", getFloorplanAssetFileName(asset)),
				contentType: asset.mimeType || "application/octet-stream"
			}
		};
	});
	const resolvableDraft = attachDriveResourcesToDraft(prepared.draft, resourcesByAssetId);
	return validateFloorplanDraft(resolvableDraft);
}

function makeImportPaths(targetPath, projectFolderName) {
	const projectPath = joinFloorplanDrivePath(targetPath, projectFolderName);
	return {
		projectPath,
		assetPath: joinFloorplanDrivePath(projectPath, "Authoring", "assets"),
		sourcePath: joinFloorplanDrivePath(projectPath, "Sources", "Legacy"),
		draftFileName: `${projectFolderName}.floorplan.json`,
		draftPath: joinFloorplanDrivePath(projectPath, `${projectFolderName}.floorplan.json`)
	};
}

function findDestinationCollision(targetPath, projectFolderName) {
	if (!targetPath || !projectFolderName) {
		return null;
	}
	let fullProjectPath;
	try {
		fullProjectPath = getFullObjectPath(joinFloorplanDrivePath(targetPath, projectFolderName));
	} catch (error) {
		return null;
	}
	const prefix = `${fullProjectPath.replace(/\/+$/, "")}/`;
	const resources = getDriveObjectsAsMap() || {};
	return Object.keys(resources).map(guid => resources[guid]).find(resource => {
		const path = getResourcePath(resource).replace(/\/+$/, "");
		return path === fullProjectPath || path.indexOf(prefix) === 0;
	}) || null;
}

export class FloorplanImportModal extends PureComponent {
	constructor(props) {
		super(props);
		this.state = initialState();
		this.reader = null;
		this.prepareRequest = 0;
		this.unmounted = false;
		this.handleClose = this.handleClose.bind(this);
		this.handleFileChange = this.handleFileChange.bind(this);
		this.handleFolderNameChange = this.handleFolderNameChange.bind(this);
		this.handleImport = this.handleImport.bind(this);
	}

	componentDidUpdate(previousProps) {
		if (this.props.open && !previousProps.open) {
			this.reset();
		}
	}

	componentWillUnmount() {
		this.unmounted = true;
		this.prepareRequest++;
		if (this.reader && this.reader.readyState === FileReader.LOADING) {
			this.reader.abort();
		}
	}

	setStateIfMounted(nextState, callback) {
		if (!this.unmounted) {
			this.setState(nextState, callback);
		}
	}

	reset() {
		this.prepareRequest++;
		if (this.reader && this.reader.readyState === FileReader.LOADING) {
			this.reader.abort();
		}
		this.reader = null;
		this.setStateIfMounted({ ...initialState(), fileInputKey: this.state.fileInputKey + 1 });
	}

	handleClose() {
		if (this.state.phase === "importing") {
			return;
		}
		this.reset();
		this.props.onClose();
	}

	handleFolderNameChange(event) {
		this.setState({
			projectFolderName: sanitizeProjectFolderName(event.target.value),
			error: "",
			result: null
		});
	}

	async handleFileChange(event) {
		const file = event.target.files && event.target.files[0];
		const requestId = ++this.prepareRequest;
		if (!file) {
			this.setState({ ...initialState(), fileInputKey: this.state.fileInputKey });
			return;
		}
		if (!isSupportedFile(file)) {
			this.setState({
				...initialState(),
				fileInputKey: this.state.fileInputKey + 1,
				error: "Choose a .floorplanproject.json or .json floorplan project file."
			});
			return;
		}

		const projectFolderName = deriveProjectFolderName(file.name);
		this.setState({
			selectedFile: file,
			projectFolderName,
			prepared: null,
			preflightValidation: null,
			phase: "reading",
			progress: { completed: 0, total: 0, loaded: 0, bytesTotal: file.size, label: "Reading the project file…" },
			error: "",
			result: null
		});

		try {
			let sourceText = await readFileAsText(
				file,
				(loaded, bytesTotal) => {
					if (requestId === this.prepareRequest) {
						this.setStateIfMounted({ progress: { completed: 0, total: 0, loaded, bytesTotal, label: "Reading the project file…" } });
					}
				},
				reader => { this.reader = reader; }
			);
			if (requestId !== this.prepareRequest) {
				return;
			}
			this.reader = null;
			this.setStateIfMounted({
				phase: "preparing",
				progress: { completed: 0, total: 0, loaded: file.size, bytesTotal: file.size, label: "Extracting and deduplicating layers…" }
			});

			let sourceProject;
			try {
				sourceProject = JSON.parse(sourceText);
			} finally {
				sourceText = null;
			}
			let prepared = await prepareLegacyFloorplanProject(sourceProject, { sourceFileName: file.name });
			sourceProject = null;
			if (requestId !== this.prepareRequest) {
				return;
			}
			const renditions = await prepareFloorplanPreviewRenditions(prepared.assets, {
				onProgress: renditionProgress => {
					if (requestId === this.prepareRequest) {
						this.setStateIfMounted({
							progress: {
								completed: renditionProgress.completed,
								total: renditionProgress.total,
								loaded: file.size,
								bytesTotal: file.size,
								label: renditionProgress.label
							}
						});
					}
				}
			});
			prepared = { ...prepared, renditions };
			if (requestId !== this.prepareRequest) {
				return;
			}
			const paths = makeImportPaths(this.props.targetPath, projectFolderName);
			const preflightValidation = validatePreparedDraft(prepared, paths.projectPath);
			this.setStateIfMounted({
				prepared,
				preflightValidation,
				phase: "ready",
				progress: { completed: 0, total: asArray(prepared.assets).length + asArray(prepared.renditions && prepared.renditions.assets).length + 1, loaded: file.size, bytesTotal: file.size, label: "Ready to import" }
			});
		} catch (error) {
			if (requestId === this.prepareRequest) {
				this.reader = null;
				this.setStateIfMounted({
					prepared: null,
					preflightValidation: null,
					phase: "error",
					error: getErrorMessage(error, "The selected file is not a valid legacy floorplan project.")
				});
			}
		}
	}

	async handleImport() {
		const { prepared, selectedFile, keepLegacySource } = this.state;
		const projectFolderName = sanitizeProjectFolderName(this.state.projectFolderName);
		if (!prepared || !selectedFile || !projectFolderName) {
			return null;
		}
		const collision = findDestinationCollision(this.props.targetPath, projectFolderName);
		if (collision) {
			this.setState({ error: `That project location already contains Drive content (${getResourcePath(collision)}). Choose a different project folder name.` });
			return null;
		}

		this.setState({
			phase: "importing",
			error: "",
			result: null,
			progress: { completed: 0, total: asArray(prepared.assets).length + asArray(prepared.renditions && prepared.renditions.assets).length + (keepLegacySource ? 2 : 1), loaded: 0, bytesTotal: 0, label: "Starting import…" }
		});
		let result;
		try {
			result = await importPreparedFloorplanProject({
				prepared,
				selectedFile,
				targetPath: this.props.targetPath,
				projectFolderName,
				keepLegacySource,
				onProgress: progress => this.setStateIfMounted({ progress: { ...this.state.progress, ...progress } })
			});
		} catch (error) {
			const uploadedCount = Number(error.uploadedCount) || 0;
			const partialFailure = uploadedCount > 0
				? ` ${uploadedCount} supporting asset${uploadedCount === 1 ? " was" : "s were"} uploaded to ${error.projectPath || "the destination"}, but the root Floorplan Studio document was not created. Remove the incomplete assets before retrying, or choose a different project folder name.`
				: " No Floorplan Studio document was created.";
			this.setStateIfMounted({
				phase: "error",
				error: `${getErrorMessage(error)}${partialFailure}`
			});
			return null;
		}

		// The prepared project retains every decoded layer byte. Drop those bytes as
		// soon as the typed root exists instead of keeping a large import in memory.
		this.setStateIfMounted({
			phase: "complete",
			result,
			prepared: null,
			preflightValidation: null,
			selectedFile: null
		});
		if (this.props.onImported) {
			try {
				await this.props.onImported(result.resource);
			} catch (error) {
				console.warn("Floorplan imported, but its post-import handler failed", error);
			}
		}
		return result;
	}

	renderProgress() {
		const { phase, progress } = this.state;
		if (["reading", "preparing", "importing"].indexOf(phase) === -1) {
			return null;
		}
		let percent = 0;
		let active = phase === "preparing";
		if (phase === "reading" && progress.bytesTotal) {
			percent = Math.min(100, Math.round((progress.loaded / progress.bytesTotal) * 100));
		} else if (phase === "importing" && progress.total) {
			percent = Math.min(100, Math.round((progress.completed / progress.total) * 100));
		}
		return (
			<div style={{ marginTop: 14 }} aria-live="polite">
				<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, color: "#52606d", fontSize: 12 }}>
					<span>{progress.label}</span>
					{!active && <span>{percent}%</span>}
				</div>
				<ProgressBar now={active ? 100 : percent} active={active} striped={active || phase === "importing"} />
			</div>
		);
	}

	renderPreflight() {
		const { prepared, preflightValidation, selectedFile } = this.state;
		if (!prepared || !selectedFile) {
			return null;
		}
		const inventory = normalizeInventory(prepared);
		const warnings = inventory.warnings.concat(asArray(preflightValidation && preflightValidation.warnings));
		const errors = asArray(preflightValidation && preflightValidation.errors);
		return (
			<div style={{ marginTop: 16 }}>
				<div style={{ padding: 14, border: "1px solid #d8e0e8", borderRadius: 8, background: "#f8fafc" }}>
					<div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase", color: "#52606d", marginBottom: 10 }}>Preflight summary</div>
					<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8 }}>
						{[
							["Floors", inventory.floors],
							["Base plans", inventory.basePlans],
								["Layers", inventory.layers],
								["Unique assets", inventory.assets],
								["Asset data", formatBytes(inventory.bytes)],
								["Studio previews", inventory.previews],
								["Preview data", formatBytes(inventory.previewBytes)]
						].map(item => (
							<div key={item[0]} style={{ padding: 9, background: "white", border: "1px solid #e1e7ec", borderRadius: 6 }}>
								<div style={{ color: "#6b7785", fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>{item[0]}</div>
								<div style={{ color: "#243b53", fontSize: 18, fontWeight: 700 }}>{item[1]}</div>
							</div>
						))}
					</div>
				</div>
				{warnings.length > 0 && (
					<Alert bsStyle="warning" style={{ marginTop: 12, marginBottom: 0 }}>
						<strong>{warnings.length} warning{warnings.length === 1 ? "" : "s"}</strong>
						<ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 20 }}>
							{warnings.slice(0, 8).map((warning, index) => <li key={index}>{issueMessage(warning)}</li>)}
							{warnings.length > 8 && <li>{warnings.length - 8} additional warnings</li>}
						</ul>
					</Alert>
				)}
				{errors.length > 0 && (
					<Alert bsStyle="danger" style={{ marginTop: 12, marginBottom: 0 }}>
						<strong>The project cannot be imported yet.</strong>
						<ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 20 }}>
							{errors.slice(0, 8).map((error, index) => <li key={index}>{issueMessage(error)}</li>)}
						</ul>
					</Alert>
				)}
			</div>
		);
	}

	render() {
		const { open, targetPath } = this.props;
		const {
			selectedFile,
			fileInputKey,
			projectFolderName,
			keepLegacySource,
			prepared,
			preflightValidation,
			phase,
			error,
			result
		} = this.state;
		const busy = phase === "reading" || phase === "preparing" || phase === "importing";
		const importing = phase === "importing";
		const collision = prepared && projectFolderName ? findDestinationCollision(targetPath, projectFolderName) : null;
		const validationErrors = asArray(preflightValidation && preflightValidation.errors);
		const canImport = !!(prepared && selectedFile && projectFolderName && validationErrors.length === 0 && !collision && !busy && !result);
		const paths = projectFolderName ? makeImportPaths(targetPath, projectFolderName) : null;

		return (
			<Modal
				open={open}
				onClose={importing ? () => {} : this.handleClose}
				closeOnEsc={!importing}
				closeOnOverlayClick={!importing}
				closeButton={!importing}
				center
				classNames={{ modal: "floorplan-import-modal" }}
				styles={{ modal: { width: "calc(100vw - 48px)", maxWidth: "760px", padding: "24px", borderRadius: "10px" } }}
			>
				<div style={{ maxHeight: "88vh", overflowY: "auto", paddingRight: 4 }}>
					<div style={{ paddingRight: 32 }}>
						<div style={{ color: "#2563eb", fontSize: 10, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase" }}>Floorplan Studio</div>
						<h3 style={{ margin: "3px 0 6px" }}>Import a floorplan project</h3>
						<p style={{ color: "#52606d", margin: "0 0 18px" }}>
							Choose a legacy Claude floorplan project. Its embedded layers will become separately managed Drive assets with optimized Studio previews, and the Studio document will be created only after every file passes validation and uploads successfully.
						</p>
					</div>

					<FormGroup controlId="floorplan-project-file">
						<ControlLabel>Legacy project file</ControlLabel>
						<FormControl
							key={fileInputKey}
							type="file"
							accept=".floorplanproject.json,.json,application/json"
							disabled={busy || !!result}
							onChange={this.handleFileChange}
						/>
						{selectedFile && <div style={{ color: "#6b7785", fontSize: 12, marginTop: 5 }}>{selectedFile.name} · {formatBytes(selectedFile.size)}</div>}
					</FormGroup>

					<FormGroup controlId="floorplan-project-folder">
						<ControlLabel>Project folder name</ControlLabel>
						<FormControl
							type="text"
							value={projectFolderName}
							disabled={busy || !!result}
							onChange={this.handleFolderNameChange}
							placeholder="The Portland"
						/>
						<div style={{ color: "#6b7785", fontSize: 12, marginTop: 5 }}>
							{paths ? `Destination: ${paths.projectPath}` : `Destination folder: ${targetPath}`}
						</div>
					</FormGroup>

					<Checkbox
						checked={keepLegacySource}
						disabled={busy || !!result}
						onChange={event => this.setState({ keepLegacySource: event.target.checked })}
					>
						Keep a copy of the original project under Sources/Legacy
						<div style={{ color: "#6b7785", fontSize: 11 }}>Off by default because legacy files can be very large. The imported Studio document does not require this copy.</div>
					</Checkbox>

					{this.renderProgress()}
					{this.renderPreflight()}

					{collision && (
						<Alert bsStyle="danger" style={{ marginTop: 12, marginBottom: 0 }}>
							The destination already contains <strong>{getResourcePath(collision)}</strong>. Choose a unique project folder name so this import cannot overwrite an existing project.
						</Alert>
					)}
					{error && <Alert bsStyle="danger" style={{ marginTop: 12, marginBottom: 0 }}>{error}</Alert>}
					{result && (
						<Alert bsStyle="success" style={{ marginTop: 12, marginBottom: 0 }}>
							<strong>Floorplan imported.</strong> The Studio document is available at {result.projectPath}.
						</Alert>
					)}

					<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18, paddingTop: 14, borderTop: "1px solid #d8e0e8" }}>
						<Button onClick={this.handleClose} disabled={importing}>{result ? "Close" : "Cancel"}</Button>
						{!result && (
							<Button bsStyle="primary" disabled={!canImport} onClick={this.handleImport}>
								{importing ? "Importing…" : "Import into Drive"}
							</Button>
						)}
					</div>
				</div>
			</Modal>
		);
	}
}

FloorplanImportModal.propTypes = {
	open: PropTypes.bool,
	targetPath: PropTypes.string.isRequired,
	onClose: PropTypes.func.isRequired,
	onImported: PropTypes.func
};

FloorplanImportModal.defaultProps = {
	open: false,
	onImported: null
};

export default FloorplanImportModal;
